import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { Box, FormControlLabel, Stack, Switch, Typography } from "@mui/material";

import { RangeType, useAsu } from "@framework/provider-asu";
import { useSettings } from "@framework/provider-settings";
import { useApiCall } from "@framework/react-hooks";

import { useAlarmPaletteResolver } from "../../../../hooks/use-alarm-color";
import { IAnomalyData } from "../scheme/components/state-and-forecast/interfaces";
import { IDefectsCountDataResponse } from "../scheme/components/defects/interfaces";
import { buildForecastWeekStartTimestamp } from "./utils";
import { ForecastingDefectIcons } from "./defect-icons";
import type { IForecastingDefectsData } from "./interfaces";

interface IAnomalySummary {
  total: number;
  high: number;
  medium: number;
  low: number;
  worstStatus: number;
}

interface IDefectSummary {
  total: number;
  critical: number;
  medium: number;
  low: number;
  worstStatus: number;
}

const FORECAST_MEDIUM_STATUS = 45;

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const normalizeTrainId = (value: unknown): number | null => {
  const id = Number(value);

  return Number.isFinite(id) && id > 0 ? id : null;
};

const getAnomalySummary = (data: IAnomalyData | null): IAnomalySummary => {
  const summary: IAnomalySummary = {
    total: 0,
    high: 0,
    medium: 0,
    low: 0,
    worstStatus: 0,
  };

  if (!data) {
    return summary;
  }

  summary.total = data.count;
  summary.high = data.l3;
  summary.medium = data.l2;
  summary.low = data.l1;
  summary.worstStatus = data.l3 ? 90 : data.l2 ? FORECAST_MEDIUM_STATUS : data.l1 ? 30 : 0;

  return summary;
};

const getDefectSummary = (data: IDefectsCountDataResponse | null): IDefectSummary => {
  return {
    total: data?.totalCount ?? 0,
    critical: data?.critical ?? 0,
    medium: data?.medium ?? 0,
    low: data?.low ?? 0,
    worstStatus:
      (data?.critical ?? 0) > 0 ? 90 : (data?.medium ?? 0) > 0 ? FORECAST_MEDIUM_STATUS : (data?.low ?? 0) > 0 ? 30 : 0,
  };
};

const StatusEye: FC<{
  color: string;
  size?: number;
}> = ({ color, size = 14 }) => {
  const normalized = color.trim().toLowerCase();

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: color,
        border: normalized === "#ffffff" ? "1px solid rgba(95, 100, 114, 0.4)" : "none",
        flexShrink: 0,
      }}
    />
  );
};

const MetricCount: FC<{ color: string; value: number; invert?: boolean; compact?: boolean }> = ({
  color,
  value,
  invert = false,
  compact = false,
}) => (
  <Typography
    sx={{
      fontSize: compact ? 18 : 24,
      lineHeight: compact ? "22px" : "28px",
      fontWeight: 700,
      color,
      minWidth: 16,
      textAlign: "center",
      textShadow: invert ? "0 1px 1px rgba(0, 0, 0, 0.25)" : "none",
    }}
  >
    {value}
  </Typography>
);

const SummaryRow: FC<{
  label: string;
  statusColor: string;
  total: number;
  critical: number;
  medium: number;
  low: number;
  compact?: boolean;
}> = ({ label, total, critical, medium, low, statusColor, compact = false }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={compact ? 1 : 2}>
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
      {compact ? <Box sx={{ width: 14, flexShrink: 0 }} /> : <StatusEye color={statusColor} />}
      <Typography
        sx={{
          color: "#ffffff",
          fontSize: compact ? 16 : 24,
          lineHeight: compact ? "22px" : "28px",
        }}
      >
        {label}:
      </Typography>
    </Stack>
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
      <MetricCount compact={compact} color="#ffffff" value={total} invert />
      <MetricCount compact={compact} color={"#f44336"} value={critical} />
      <MetricCount compact={compact} color={"#ffeb3b"} value={medium} />
      <MetricCount compact={compact} color={"#66bb6a"} value={low} />
    </Stack>
  </Stack>
);

const TciScale: FC<{
  isTrendPinned: boolean;
  onTrendPinnedChange: (value: boolean) => void;
  value: number | null;
  warning: number;
  critical: number;
  resolveColor: (status: number) => string;
}> = ({ isTrendPinned, onTrendPinnedChange, value, warning, critical, resolveColor }) => {
  const { formatMessage, formatNumber } = useIntl();

  const normalizedCritical = clamp(critical, 0, 100);
  const normalizedWarning = clamp(warning, normalizedCritical, 100);
  const markerPosition = value === null ? null : clamp(value, 0, 100);
  const segments = [
    { width: normalizedCritical, color: resolveColor(90) },
    { width: normalizedWarning - normalizedCritical, color: resolveColor(60) },
    { width: 100 - normalizedWarning, color: resolveColor(30) },
  ];
  const ticks = [0, 20, 40, 60, 80, 100];

  return (
    <Box
      sx={{
        flex: "0 0 200px",
        px: 2,
        py: 1.5,
        border: "1px solid #d8dbe8",
        borderRadius: "12px",
        backgroundColor: "#f6f7fb",
      }}
    >
      <Typography
        sx={{
          fontSize: 18,
          lineHeight: "22px",
          color: "#11131a",
          whiteSpace: "nowrap",
          mb: 1,
        }}
      >
        {formatMessage({ id: "pages.forecasting.summary.tci" })}:{" "}
        {value === null ? "—" : formatNumber(Math.round(value * 100) / 100)}
      </Typography>
      <Box>
        <Box sx={{ position: "relative" }}>
          <Stack
            direction="row"
            sx={{ height: 22, borderRadius: "6px", overflow: "hidden", border: "1px solid #cfd3e4" }}
          >
            {segments.map(segment => (
              <Box
                key={`${segment.color}-${segment.width}`}
                sx={{ width: `${segment.width}%`, backgroundColor: segment.color, opacity: 0.85 }}
              />
            ))}
          </Stack>
          {markerPosition !== null ? (
            <Box
              sx={{
                position: "absolute",
                left: `${markerPosition}%`,
                top: -4,
                bottom: -4,
                width: 4,
                borderRadius: 999,
                backgroundColor: "#3b4050",
                transform: "translateX(-50%)",
                boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.9)",
              }}
            />
          ) : null}
        </Box>
      </Box>
      <Box
        sx={{
          position: "relative",
          height: 18,
          mt: 0.75,
        }}
      >
        {ticks.map(tick => (
          <Typography
            key={tick}
            sx={{
              position: "absolute",
              left: `${tick}%`,
              top: 0,
              transform: tick === 0 ? "translateX(0)" : tick === 100 ? "translateX(-100%)" : "translateX(-50%)",
              fontSize: 12,
              lineHeight: "14px",
              color: "#4e5568",
              whiteSpace: "nowrap",
            }}
          >
            {tick}
          </Typography>
        ))}
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={isTrendPinned}
            inputProps={{ "aria-label": formatMessage({ id: "pages.forecasting.pinTciTrend" }) }}
            onChange={event => onTrendPinnedChange(event.target.checked)}
            size="small"
          />
        }
        label={
          <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846" }}>
            {formatMessage({ id: "pages.forecasting.pinTciTrend" })}
          </Typography>
        }
        sx={{ m: 0, mt: 0.5 }}
      />
    </Box>
  );
};

export const ForecastingSummaryPanel: FC<{
  isTrainTciTrendPinned: boolean;
  onTrainTciTrendPinnedChange: (value: boolean) => void;
}> = ({ isTrainTciTrendPinned, onTrainTciTrendPinnedChange }) => {
  const [searchParams] = useSearchParams();
  const { formatMessage } = useIntl();
  const { selectedItem, startTimestamp, endTimestamp, userSelectedRangeType, recorderWindowStart } = useAsu();
  const settings = useSettings();
  const resolveAlarmColor = useAlarmPaletteResolver();

  const [status, setStatus] = useState(0);
  const [tci, setTci] = useState<number | null>(null);
  const [defectsData, setDefectsData] = useState<IForecastingDefectsData | null>(null);
  const [weeklyAnomalySummary, setWeeklyAnomalySummary] = useState<IAnomalySummary>(getAnomalySummary(null));
  const defectSummary = getDefectSummary(defectsData);
  const predictiveSummary = getDefectSummary(defectsData?.predictive ?? null);

  const { warning, critical } = settings.getTCILevels();
  const selectedTrainId = selectedItem && "TrainId" in selectedItem ? normalizeTrainId(selectedItem.TrainId) : null;
  const trainId = selectedTrainId ?? normalizeTrainId(searchParams.get("trainId"));
  const trainName = selectedItem && "TrainName" in selectedItem ? selectedItem.TrainName ?? "" : "";
  const effectiveStartTimestamp =
    userSelectedRangeType === RangeType.RECORDER_MODE ? recorderWindowStart : startTimestamp;
  const weekStartTimestamp = buildForecastWeekStartTimestamp(endTimestamp);

  const { fn: getStatus, isLoading: isStatusLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string | Date; endTs: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: `/trains/${idTrain}/status`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
            useForAlert: true,
          },
        })) as { AlarmStatus: number | null } | null;

        return json?.AlarmStatus ?? 0;
      } catch (error) {
        console.error(error);
        return 0;
      }
    },
    { success: false, error: false },
  );

  const { fn: getTci, isLoading: isTciLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string | Date; endTs: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: `/trains/${idTrain}/tci`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
          },
        })) as { TCIVal: number | null } | null;

        return typeof json?.TCIVal === "number" ? json.TCIVal : null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    { success: false, error: false },
  );

  const { fn: getWeeklyAnomalyData, isLoading: isWeeklyAnomalyLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string; endTs: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: `/predictive/anomaly/${idTrain}`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
          },
        })) as IAnomalyData | null;

        return json;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    { success: false, error: false },
  );

  const { fn: getDefects, isLoading: isDefectsLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string | Date; endTs: string | Date }) => {
      try {
        return (await api.fetchJson({
          url: `/predictive/defects/${idTrain}`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
          },
        })) as IForecastingDefectsData;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    { success: false, error: false },
  );

  useEffect(() => {
    setStatus(0);
    setTci(null);
    setWeeklyAnomalySummary(getAnomalySummary(null));
    setDefectsData(null);

    if (!trainId) return;
    if (userSelectedRangeType === RangeType.RECORDER_MODE && !recorderWindowStart) return;
    if (!effectiveStartTimestamp || !weekStartTimestamp) return;

    let active = true;
    const requests = [
      getStatus(undefined, { idTrain: trainId, startTs: effectiveStartTimestamp, endTs: endTimestamp }).then(
        nextStatus => {
          if (active && typeof nextStatus === "number") {
            setStatus(nextStatus);
          }
        },
      ),
      getTci(undefined, { idTrain: trainId, startTs: effectiveStartTimestamp, endTs: endTimestamp }).then(
        nextTci => active && setTci(typeof nextTci === "number" ? nextTci : null),
      ),
      getWeeklyAnomalyData(undefined, { idTrain: trainId, startTs: weekStartTimestamp, endTs: endTimestamp }).then(
        data => active && setWeeklyAnomalySummary(getAnomalySummary(data ?? null)),
      ),
      getDefects(undefined, { idTrain: trainId, startTs: effectiveStartTimestamp, endTs: endTimestamp }).then(
        defects => active && setDefectsData(defects ?? null),
      ),
    ];

    void Promise.allSettled(requests);

    return () => {
      active = false;
    };
  }, [trainId, endTimestamp, effectiveStartTimestamp, recorderWindowStart, userSelectedRangeType, weekStartTimestamp]);

  const loading = isStatusLoading || isTciLoading || isWeeklyAnomalyLoading || isDefectsLoading;

  if (!trainId) {
    return null;
  }

  return (
    <Box
      sx={{
        flexShrink: 0,
        px: { xs: 1, md: 2 },
        pt: { xs: 1, md: 1.5 },
        pb: 1,
        opacity: loading ? 0.7 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            flex: "1 1 460px",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.5, md: 2 },
            border: "1px solid #d8dbe8",
            borderRadius: "14px",
            background: "linear-gradient(180deg, #ffffff 0%, #f7f8fd 100%)",
          }}
        >
          <Box
            sx={{ display: "flex", flex: "1 1 auto", minWidth: 0, alignItems: "center", flexWrap: "wrap", gap: 1.5 }}
          >
            <Typography
              noWrap
              title={trainName}
              sx={{
                flex: "0 1 auto",
                fontWeight: 700,
                fontSize: { xs: 28, md: 34 },
                lineHeight: { xs: "32px", md: "38px" },
                color: "#11131a",
                minWidth: 0,
              }}
            >
              {trainName}
            </Typography>
            <ForecastingDefectIcons
              icons={defectsData?.detectedIcons ?? []}
              label={formatMessage({ id: "pages.forecasting.summary.detectedDefects" })}
            />
            <ForecastingDefectIcons
              icons={defectsData?.predictedIcons ?? []}
              label={formatMessage({ id: "pages.forecasting.summary.predictedDefects" })}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: "1 1 440px",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 1,
            px: 2,
            py: 1.5,
            border: "1px solid #4a4f5f",
            borderRadius: "14px",
            backgroundColor: "#363846",
          }}
        >
          <SummaryRow
            label={formatMessage({ id: "pages.forecasting.summary.anomaliesWeek" })}
            statusColor={resolveAlarmColor(weeklyAnomalySummary.worstStatus)}
            total={weeklyAnomalySummary.total}
            critical={weeklyAnomalySummary.high}
            medium={weeklyAnomalySummary.medium}
            low={weeklyAnomalySummary.low}
          />
          <SummaryRow
            label={formatMessage({ id: "pages.forecasting.summary.defectsDetected" })}
            statusColor={resolveAlarmColor(defectSummary.worstStatus)}
            total={defectSummary.total}
            critical={defectSummary.critical}
            medium={defectSummary.medium}
            low={defectSummary.low}
          />
          <SummaryRow
            compact
            label={formatMessage({ id: "pages.forecasting.summary.predictiveDefects" })}
            statusColor={resolveAlarmColor(predictiveSummary.worstStatus)}
            total={predictiveSummary.total}
            critical={predictiveSummary.critical}
            medium={predictiveSummary.medium}
            low={predictiveSummary.low}
          />
        </Box>

        <Box
          sx={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1,
            py: 1.5,
            border: "1px solid #d8dbe8",
            borderRadius: "14px",
            backgroundColor: "#f1f2f6",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 18, lineHeight: "22px", color: "#11131a" }}>
              {formatMessage({ id: "pages.forecasting.summary.gost" })}:
            </Typography>
            <StatusEye color={resolveAlarmColor(status)} size={26} />
          </Stack>
        </Box>
        <TciScale
          critical={critical}
          isTrendPinned={isTrainTciTrendPinned}
          onTrendPinnedChange={onTrainTciTrendPinnedChange}
          resolveColor={resolveAlarmColor}
          value={tci}
          warning={warning}
        />
      </Box>
    </Box>
  );
};
